import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getFilterOptions, getPropertyTypes } from '../../../new-lib/apis';
import styled from 'styled-components';
import { GetStaticPropsContext } from 'next';
import { useRef } from 'react';
import { wrapper } from '../../../redux/store';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { useSelector } from 'react-redux';
import ImageWrapper from '../../../new-components/ImageWrapper';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';
import NawyUnlockedForm from '../../../features/contact/components/nawy-unlocked/NawyUnlockedForm';

interface WrapperProps {
  locale?: string;
}

const NawyUnlocked = (props: any) => {
  const { t } = useTranslation('nawy-unlocked');
  const router = useRouter();
  const { locale } = router;
  const formRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const renderStep = (
    imageSrc: string,
    titleKey: string,
    descriptionKey: string
  ) => (
    <div className="step">
      <ImageWrapper
        src={`/assets/icons/nawy-unlocked/${imageSrc}`}
        alt={imageSrc}
        width={240}
        height={100}
      />
      <span>{t(titleKey)}</span>
      <p>{t(descriptionKey)}</p>
    </div>
  );

  const renderMobileStep = (
    imageSrc: string,
    titleKey: string,
    descriptionKey: string
  ) => (
    <div className="step">
      <div className="row-container">
        <ImageWrapper
          src={`/assets/icons/nawy-unlocked/${imageSrc}`}
          alt={imageSrc}
          width={50}
          height={45}
        />
        <span>{t(titleKey)}</span>
      </div>
      <p>{t(descriptionKey)}</p>
    </div>
  );

  return (
    <Wrapper locale={locale}>
      <div className="introduction">
        <div className={`header container-fluid`}>
          <div className="header-title">
            <h1>{t('nawy_unlocked')}</h1>
          </div>
          <p>{t('nawy_unlocked_description')}</p>
        </div>
        {!isMobile ? (
          <div className="steps container-fluid">
            {renderStep(
              'finishing.svg',
              'step_one_title',
              'step_one_description'
            )}
            {renderStep('rental.svg', 'step_two_title', 'step_two_description')}
            {renderStep(
              'home.svg',
              'step_three_title',
              'step_three_description'
            )}
            {renderStep(
              'dollar.svg',
              'step_four_title',
              'step_four_description'
            )}
          </div>
        ) : (
          <div className="container-fluid">
            <div className="steps">
              {renderMobileStep(
                'finishing.svg',
                'step_one_title',
                'step_one_description'
              )}
              {renderMobileStep(
                'rental.svg',
                'step_two_title',
                'step_two_description'
              )}
              {renderMobileStep(
                'home.svg',
                'step_three_title',
                'step_three_description'
              )}
              {renderMobileStep(
                'dollar.svg',
                'step_four_title',
                'step_four_description'
              )}
            </div>
          </div>
        )}
      </div>
      <div className="container-fluid" ref={formRef}>
        <NawyUnlockedForm
          propertyTypes={props.propertyTypes?.map((propertyType: any) => ({
            value: propertyType.id,
            label: propertyType.name
          }))}
          finishingTypes={props.finishingTypes?.map((propertyType: any) => ({
            value: propertyType.value,
            label: t(propertyType.label, { ns: 'common' })
          }))}
        />
      </div>
    </Wrapper>
  );
};
export default NawyUnlocked;

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;

  .modal-content {
    padding: 0px 0px !important;
  }

  .introduction {
    display: flex;
    flex-direction: column;
    background: #f0f1f3;
    justify-content: center;
    align-items: flex-start;
    gap: 32px;
    padding: 64px 32px 40px 32px;
  }
  .header {
    .header-title {
      display: flex;
      justify-content: space-between;
    }
    h1 {
      color: #015c9a;
      font-family: 'materialBold';
      font-size: 40px;
      line-height: normal;
      text-transform: capitalize;
    }
    button {
      color: #015c9a;
      text-align: center;
      font-family: 'materialRegular';
      font-size: 16px;
      line-height: normal;
      text-decoration-line: underline;
      text-transform: capitalize;
      background: transparent;
      border-color: transparent;
    }

    p {
      color: #2c3138;
      font-family: 'materialMedium';
      font-size: 18px;
      line-height: normal;
      text-transform: capitalize;
    }
  }
  .steps {
    display: flex;
    margin-top: 80px;
    gap: 16px;
    .step {
      position: relative;
      display: flex;
      padding: 54px 16px 24px 16px;
      flex-direction: column;
      align-items: center;
      gap: 24px;
      border-radius: 12px;
      background: #fff;
      width: 40%;
      img {
        position: absolute;
        top: -40%;
      }
      span {
        color: #2c3138;
        text-align: center;
        font-family: materialSemiBold;
        font-size: 20px;
        text-transform: capitalize;
      }
      p {
        color: #2c3138;
        text-align: center;
        font-family: materialRegular;
        font-size: 15px;
      }
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    .introduction {
      display: flex;
      padding: 24px 0px;
      flex-direction: column;
      align-items: flex-start;
      overflow-x: hidden;
      gap: 15px;
    }
    .header {
      margin-top: 0px;
      display: flex;
      padding: 0px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;

      h1 {
        color: #015c9a;
        font-family: materialBold;
        font-size: 28px;
        text-transform: capitalize;
        text-align: left;
      }
      p {
        color: #2c3138;
        font-family: materialRegular;
        font-size: 13px;
      }
    }
    .steps {
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
      margin-top: 16px;
      gap: 16px;
      .step {
        display: flex;
        min-width: 70%;
        padding: 16px;
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
        border-radius: 12px;
        background: #fff;
        .row-container {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        img {
          position: relative;
          top: 0;
        }
        span {
          color: #2c3138;
          text-align: center;
          font-family: materialSemiBold;
          font-size: 14px;
          text-transform: capitalize;
          text-align: ${props => (props.locale === 'en' ? 'left' : 'right')};
          margin: 0px 5px;
        }
        p {
          color: #2c3138;
          text-align: center;
          font-family: materialRegular;
          font-size: 13px;
          text-align: ${props => (props.locale === 'en' ? 'left' : 'right')};
        }
      }
    }
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [propertyTypes, filterOptions] = await Promise.all([
        getPropertyTypes(locale),
        getFilterOptions(locale)
      ]);

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        revalidate: 60 * 60,
        props: {
          ...(await serverSideTranslations(locale as string, [
            'nawy-unlocked',
            'common',
            'search',
            'entity',
            'contact'
          ])),
          propertyTypes,
          finishingTypes: filterOptions.finishing?.map((item: any) => ({
            value: item.value,
            label: item.options.displayedValue
          })),
          filterOptions: JSON.stringify(filterOptions) || '',
          layout: {
            footerLinks: [],
            url: `${process.env.NEXT_PUBLIC_HOST}/nawy-unlocked`
          }
        }
      };
    }
);

export async function getStaticPaths() {
  return { paths: StaticPagesUtility.get(), fallback: false };
}
